import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import styled from 'styled-components';
import { auth } from '../../firebase/config';
import ieumLogo from '../../assets/ieum-logo.png';

const DEFAULT_HANDOFF_ENDPOINT = 'https://ieum-auto.in/realtorauto/api/realtorauto/web-session-handoff/';

const handoffEndpoint = () => {
  const configured = String(process.env.REACT_APP_REALTORAUTO_WEB_SESSION_HANDOFF_URL || '').trim();
  try {
    const parsed = new URL(configured || DEFAULT_HANDOFF_ENDPOINT);
    if (parsed.protocol !== 'https:' || !['ieum-auto.in', 'www.ieum-auto.in'].includes(parsed.hostname.toLowerCase())) {
      return DEFAULT_HANDOFF_ENDPOINT;
    }
    parsed.searchParams.set('action', 'issue');
    return parsed.toString();
  } catch {
    return `${DEFAULT_HANDOFF_ENDPOINT}?action=issue`;
  }
};

const safeConsumeUrl = (rawUrl) => {
  try {
    const parsed = new URL(String(rawUrl || ''));
    if (parsed.protocol !== 'https:' || !['ieum-auto.in', 'www.ieum-auto.in'].includes(parsed.hostname.toLowerCase())) return '';
    if (parsed.pathname !== '/realtorauto/api/realtorauto/web-session-handoff/') return '';
    if (parsed.searchParams.get('action') !== 'consume') return '';
    return parsed.toString();
  } catch {
    return '';
  }
};

const RealtorAutoConnectPage = () => {
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let submitted = false;
    const state = new URLSearchParams(window.location.search).get('state') || '';
    if (!/^[A-Za-z0-9_-]{32,128}$/u.test(state)) {
      setError('로그인 연결 요청이 올바르지 않습니다. 부동산오토에서 다시 시작해 주세요.');
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (cancelled || submitted) return;
      if (!user) {
        const loginUrl = new URL('/login', window.location.origin);
        loginUrl.searchParams.set('continueUrl', window.location.href);
        window.location.replace(loginUrl.toString());
        return;
      }
      submitted = true;
      try {
        setError('');
        const idToken = await user.getIdToken(false);
        if (cancelled) return;
        const response = await fetch(handoffEndpoint(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'omit',
          body: JSON.stringify({ state, idToken }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(payload?.error || '부동산오토 로그인 연결을 완료하지 못했습니다.');
        }
        const consumeUrl = safeConsumeUrl(payload?.consumeUrl);
        if (!consumeUrl) throw new Error('부동산오토 복귀 주소가 올바르지 않습니다.');
        if (!cancelled) window.location.replace(consumeUrl);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError?.message || '부동산오토 로그인 연결 중 오류가 발생했습니다.');
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [retryKey]);

  return (
    <Page>
      <Card>
        <Logo src={ieumLogo} alt="이음" />
        <Title>부동산오토 로그인 연결</Title>
        {error ? (
          <>
            <ErrorBox>{error}</ErrorBox>
            <RetryButton type="button" onClick={() => setRetryKey((value) => value + 1)}>
              다시 연결
            </RetryButton>
            <FallbackLink href="https://ieum-auto.in/realtorauto/?realtorautoHandoff=failed&handoffReason=manual">
              부동산오토 직접 로그인
            </FallbackLink>
          </>
        ) : (
          <Status role="status" aria-live="polite">
            <Spinner aria-hidden />
            이음 로그인 상태를 부동산오토에 안전하게 연결하고 있습니다.
          </Status>
        )}
      </Card>
    </Page>
  );
};

export default RealtorAutoConnectPage;

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f5f7fb;
`;

const Card = styled.section`
  width: min(100%, 420px);
  padding: 36px 28px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.1);
  text-align: center;
`;

const Logo = styled.img`
  width: 112px;
  height: auto;
`;

const Title = styled.h1`
  margin: 22px 0 18px;
  color: #1f2937;
  font-size: 20px;
  font-weight: 700;
`;

const Status = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.6;
`;

const Spinner = styled.span`
  width: 30px;
  height: 30px;
  border: 3px solid #dbe4ff;
  border-top-color: #4a6cf7;
  border-radius: 999px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorBox = styled.div`
  padding: 14px 16px;
  border: 1px solid #fecaca;
  border-radius: 10px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  line-height: 1.6;
`;

const RetryButton = styled.button`
  width: 100%;
  margin-top: 18px;
  padding: 13px 16px;
  border: 0;
  border-radius: 10px;
  background: #4a6cf7;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const FallbackLink = styled.a`
  display: inline-block;
  margin-top: 16px;
  color: #6b7280;
  font-size: 13px;
`;
